/* eslint-disable no-underscore-dangle */
import React, {
  useRef,
  useCallback,
  useEffect,
  useState
} from 'react';
import FullCalendar from '@fullcalendar/react';
import {
  ATMGrid,
  ATMHeader,
  ATMSegment,
  IORGDataTableQueryState,
} from 'shared-it-appmod-ui';
import { useLocation } from 'react-router-dom';
import {
  CalendarView,
  FlightCategory,
  MonthViewEvents,
  RequestFlightPathName,
  RequestStatus
} from 'src/constants';
import useTabLocation from 'src/hooks/tab-location.hook';
import Moment from 'src/libraries/moment.library';
import { useRequestContext } from 'src/contexts/request.context';
import Lang from 'src/libraries/language';
import { actionTypes } from 'src/ducks/request.duck';
import { getMappedEvent, getMappedMonthViewEvents } from 'src/selectors/flights-calendar.selector';
import { getCSVContent, getRequestStatus, getTableListParams } from 'src/selectors/request.selector';
import FlightsCalendarFilter from './flights-calendar-filter/flights-calendar-filter.component';
import FlightsCalendarView from './flights-calendar.view';
import style from './flights-calendar.module.scss';

const checkValue = (value: any): string => {
  if (value !== null && value !== undefined) {
    return value.toString();
  }
  return '';
};

const oneColumnData = (data: any[], field: string): string => {
  const rtrnVl = data.map((value) => {
    return checkValue(value[field]);
  });
  return rtrnVl.toString();
};

const isFlagValue = (flag: any): string => {
  if (flag !== null && flag !== undefined) {
    return flag.toLowerCase() === 'true' ? 'yes' : 'no';
  }
  return '';
};

const FlightsCalendar: React.FC = () => {
  const location = useLocation();
  const { currentTab } = useTabLocation();
  const { state, actions } = useRequestContext();
  const [calendarView, setCalendarView] = useState(CalendarView.MonthView);
  const [events, setEvents] = useState<any>([]);
  const [cacheData, setCacheData] = useState('');
  const [cleared, setCleared] = useState(true);
  const [parameters, setParameters] = useState<IORGDataTableQueryState>({
    page: 1,
    limit: 0,
  });
  const calendarComponentRef = useRef<FullCalendar>();
  const loading = getRequestStatus(state, actionTypes.REQUEST_LIST_FETCH).fetching;

  useEffect(() => {
    actions.filterGET();
  }, [actions]);

  const handleFetch = useCallback(
    (params: IORGDataTableQueryState, view?: CalendarView) => {
      if (view) {
        if (calendarComponentRef && calendarComponentRef.current) {
          calendarComponentRef.current?.getApi().changeView(view, {
            start: params.flightDateStart,
            end: params.flightDateEnd,
          });
        }
      }
      const data = getTableListParams(params, currentTab, '/flightsCalendar');
      const path = JSON.stringify(data);

      if (!data.flightDate) {
        if (!data.flightDateStart && !data.flightDateEnd) {
          data.flightDateStart = Moment().utc().startOf('month').format('YYYY-MM-DD');
          data.flightDateEnd = Moment().utc().endOf('month').format('YYYY-MM-DD');
        }
      }

      if (data.ticket || data.flightNo) {
        data.flightDate = undefined;
        data.flightDateStart = undefined;
        data.flightDateEnd = undefined;
      }
      if (cacheData !== path) {
        setCacheData(path);
        actions.listGET(data);
      }

      actions.setListPath(`${location.pathname}${location.search}`);
    },
    [actions, cacheData, setCacheData, currentTab, location]
  );

  useEffect(() => {
    if (cacheData === '' || cleared ||
      (parameters.ticket || parameters.flightNo)
    ) {
      handleFetch(parameters);
      setCleared(false);
    }
  }, [cacheData, handleFetch, parameters, cleared]);

  const handleCalendarEvents = useCallback((data) => {
    if (data.view.type !== CalendarView.MonthView) {
      const { requestorId, flightCategoryKey } = data.event._def.extendedProps;
      window.open(`${window.location.origin}/flight/${RequestFlightPathName[flightCategoryKey]}/${requestorId}/edit?from=calendar`);
    } else {
      switch (data.event._def.title) {
        case MonthViewEvents.Construction: {
          setEvents(
            getMappedEvent(state.list)
              .filter((item) =>
                item.flightCategoryKey === FlightCategory.ConstructionWork &&
                item.requestStatus !== RequestStatus.PendingApproval &&
                item.requestStatus !== RequestStatus.Received
              )
          );
          break;
        }
        case MonthViewEvents.InternalDrone: {
          setEvents(
            getMappedEvent(state.list)
              .filter((item) =>
                item.flightCategoryKey === FlightCategory.DroneFlightInternal &&
                item.requestStatus !== RequestStatus.PendingApproval &&
                item.requestStatus !== RequestStatus.Received
              )
          );
          break;
        }
        case MonthViewEvents.PatrolPassenger: {
          setEvents(
            getMappedEvent(state.list)
              .filter((item) =>
                item.flightCategoryKey === FlightCategory.PatrolPassenger &&
                item.requestStatus !== RequestStatus.PendingApproval &&
                item.requestStatus !== RequestStatus.Received
              )
          );
          break;
        }
        case MonthViewEvents.ExternalManned: {
          setEvents(
            getMappedEvent(state.list)
              .filter((item) =>
                (item.flightCategoryKey === FlightCategory.ContractorConstruction ||
                item.flightCategoryKey === FlightCategory.ContractorPatrol) &&
                item.requestStatus !== RequestStatus.PendingApproval &&
                item.requestStatus !== RequestStatus.Received
              )
          );
          break;
        }
        case MonthViewEvents.ExternalDrone: {
          setEvents(
            getMappedEvent(state.list)
              .filter((item) =>
                (item.flightCategoryKey === FlightCategory.DroneFlightExternal) &&
                item.requestStatus !== RequestStatus.PendingApproval &&
                item.requestStatus !== RequestStatus.Received
              )
          );
          break;
        }
        default: {
          setEvents(
            getMappedEvent(state.list)
              .filter((item) => (item.requestStatus === RequestStatus.PendingApproval || item.requestStatus === RequestStatus.Received))
          );
          break;
        }
      }
    }
  }, [state, setEvents]);

  const handleViewMount = useCallback((data) => {
    if (data === CalendarView.MonthView) {
      setEvents(getMappedMonthViewEvents(state.list));
    } else {
      setEvents(getMappedEvent(state.list));
    }
  }, [state, setEvents, getMappedMonthViewEvents, getMappedEvent]);

  useEffect(() => {
    handleViewMount(calendarView);
  }, [calendarView, handleViewMount]);

  const handleDownload = useCallback(
    async (params: IORGDataTableQueryState) => {
      const data = getTableListParams(params, currentTab);
      const result = await actions.listGETDownload({
        ...data,
        page: 0,
        limit: 0,
      });
      if (result.payload && result.payload.rows && result.payload.rows.length) {
        const items = result.payload.rows.map((value) => [
          Moment.utc(value.flightDate).startOf('day').format('MM/DD/YYYY'),
          checkValue(value.flightNo),
          checkValue(value.requestorDistrict),
          checkValue(value.fieldCompany),
          checkValue(value.flightCategory),
          checkValue(value.flightType),
          checkValue(value.requestorName),
          checkValue(value.operatorName),
          checkValue(value.tailNumber),
          checkValue(value.pilot),
         // checkValue(value.exu),
          checkValue(value.flightHours),
          checkValue(value.standByHours),
          checkValue(value.costCenter),
          oneColumnData(value.accounting, 'workOrder'),
          oneColumnData(value.accounting, 'ioNo'),
          oneColumnData(value.accounting, 'planCategory'),
          checkValue(value.flightCost),
          isFlagValue(value.observedFlag),
          checkValue(value.riskScore),
          isFlagValue(value.contractorMissionFlag),
        ]);

        const content = getCSVContent(items, [
          Lang.LBL_ACTUAL_FLIGHT_DATE,
          Lang.LBL_FLIGHT_NO,
          Lang.LBL_DISTRICT_PROJECT,
          Lang.LBL_REQUESTING_DEPARTMENT,
          Lang.LBL_FLIGHT_CATEGORY,
          Lang.LBL_FLIGHT_TYPE,
          Lang.LBL_REQUESTOR,
          Lang.LBL_OPERATOR,
          Lang.LBL_TAIL_NUMBER,
          Lang.LBL_FIRE_DISPATCH_PILOT,
          Lang.LBL_EXU,
          Lang.LBL_REQUEST_FLIGHT_HOURS,
          Lang.LBL_REQUEST_FLIGHT_STANDBY_HOURS,
          Lang.LBL_REQUEST_COST_CENTER,
          Lang.LBL_REQUEST_WORK_ORDER,
          Lang.LBL_REQUEST_IO_NUMBER,
          Lang.LBL_REQUEST_PLAN_CATEGORY,
          Lang.LBL_FLIGHT_COST,
          Lang.LBL_OBSERVED,
          Lang.LBL_RISK_ASSESSMENT_SCORE,
          Lang.LBL_CONTRACTOR_MISSION,
        ]);

        const link = document.createElement('a');
        link.setAttribute('href', content);
        link.setAttribute(
          'download',
          `SDGE_Aviation_Services_Flight_Report_${Moment().format('YYYYMMDD')}.xls`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    [currentTab, actions]
  );

  return (
    <>
      <ATMGrid>
        <ATMGrid.Row columns={2}>
          <ATMGrid.Column>
            <ATMHeader as="h1" content={Lang.TTL_FLIGHTS_CALENDAR} />
          </ATMGrid.Column>
          <ATMGrid.Column />
        </ATMGrid.Row>
        <ATMGrid.Row>
          <ATMGrid.Column>
            <ATMSegment className={style.fullCalendar}>
              <FlightsCalendarFilter
                loading={loading}
                filters={state.filters}
                parameters={parameters}
                total={state.list.length}
                handleDownload={handleDownload}
                handleClear={(value) => setCleared(value)}
                handleFetch={() => handleFetch(parameters)}
                handleParams={(value) => setParameters(value)}
                downloading={
                  getRequestStatus(state, actionTypes.REQUEST_LIST_FETCH_DOWNLOAD)
                    .fetching
                }
              />
              <br />
              <FlightsCalendarView
                calendarEvents={events}
                parameters={parameters}
                calendarComponentRef={calendarComponentRef}
                handleFetch={(value, view) => {
                  if (view) {
                    setCalendarView(view);
                    handleFetch(value, view);
                  }
                }}
                handleViewMount={(value) => setCalendarView(value)}
                handleCalendarEvents={(value) => handleCalendarEvents(value)}
              />
            </ATMSegment>
          </ATMGrid.Column>
        </ATMGrid.Row>
      </ATMGrid>
    </>
  );
};

export default FlightsCalendar;
